export function createPreviewCapture<
  T extends { getTracks(): { stop(): void }[] },
  P extends { destroy(): Promise<void> },
>({
  capture,
  activate,
}: {
  capture(): Promise<T>;
  activate(stream: T): Promise<P>;
}) {
  let generation = 0;
  let active:
    | { stream: T; processor?: P; stopped?: boolean; destroyed?: boolean }
    | undefined;
  const release = async (entry: {
    stream: T;
    processor?: P;
    stopped?: boolean;
    destroyed?: boolean;
  }) => {
    if (!entry.stopped) {
      entry.stopped = true;
      entry.stream.getTracks().forEach((track) => track.stop());
    }
    if (entry.processor && !entry.destroyed) {
      entry.destroyed = true;
      await entry.processor.destroy();
    }
  };
  return {
    async start() {
      const token = ++generation;
      const previous = active;
      active = undefined;
      if (previous) await release(previous);
      if (token !== generation) return;
      const stream = await capture();
      if (token !== generation) {
        await release({ stream });
        return;
      }
      const entry: { stream: T; processor?: P } = { stream };
      active = entry;
      try {
        entry.processor = await activate(stream);
      } catch (error) {
        if (active === entry) active = undefined;
        await release(entry);
        throw error;
      }
      if (token !== generation) {
        if (active === entry) active = undefined;
        await release(entry);
      }
    },
    async stop() {
      ++generation;
      const previous = active;
      active = undefined;
      if (previous) await release(previous);
    },
  };
}
